import {
  boolean,
  index,
  integer,
  jsonb,
  text,
  timestamp,
  varchar,
} from "drizzle-orm/pg-core"
import { email_length, id_length, ulidId } from "./shared"

import { coreExportSchema, coreProductGroup } from "./core_products"
import { templateRevision } from "./template"

export const transferRequest = coreExportSchema.table(
  "transfer_request",
  {
    // IDENTIFIERS
    id: ulidId(),
    certificate_number: text("certificate_number").notNull().unique(), // e.g. "NZL2025/REGID/123T"
    uniqueConsignmentReference: text("unique_consignment_reference"),
    productGroupId: varchar("product_item_group_id", {
      length: id_length,
    })
      .notNull()
      .references(() => coreProductGroup.id, { onDelete: "cascade" }),
    templateId: varchar("template_version_id", {
      length: id_length,
    })
      .notNull()
      .references(() => templateRevision.id, { onDelete: "cascade" }),
    templateCode: varchar("template_code", { length: 20 }),
    templateVersion: integer("template_version"),
    version: integer("version"),
    status: text("status", {
      // This enum needs to match the Status enum in the graphql
      enum: [
        "DRAFT", // Consignor editable status
        "RAISED",
        "CANCELLED",
        "RESUBMIT", // Consignor editable status, Deliberately corrected from REQUESTED_TO_RESUBMIT in ec schema
        "AMENDED",
        "REQUEST_AMENDMENT",
        "TO_BE_AMENDED", // Consignor editable status
        "APPROVED",
        "REVOKED",
        "REJECTED",
      ],
    }).notNull(),
    substatus: text("substatus", {
      enum: ["PENDING_SF_CALL" /*"REQUESTED_TO_REVOKE"*/],
    }),
    isExhausted: boolean("exhausted").default(false),
    // TODO: Rename eligibility_declaration to auto_approval
    eligibility_declaration: boolean("eligibility_declaration").default(false),
    auditComment: text("audit_comment"),
    urgency: jsonb("urgency"),
    reasonForStatusChange: text("reason_for_state_change"), // TODO: Determine if status history goes in request history or separate request status table

    destination_country: jsonb("destination_country"),
    commodity: text("commodity").notNull(),

    // PARTIES - Registrations
    consignorRegId: varchar("consignor_id", {
      length: id_length,
    }), // TODO - add FK to registrations table in party schema
    consignorId: jsonb("consignor_blob").notNull(),
    consigneeId: jsonb("consignee_blob"),
    consigneeRegId: varchar("consignee_id", {
      length: id_length,
    }), // TODO - add FK to registrations table in party schema
    thirdPartyConsignor: jsonb("third_party_consignor"), // Array of registration IDs for third party access

    transport: jsonb("transport"), // Contains CoreTransport GraphQL
    unofficialInformation: text("unofficial_information"),
    reasonCategories: jsonb("reason_categories"), // Array of reasons selected by user
    // Container goes on product group, as can be at consignment or product level

    /**
     * To enable history triggers
     */
    lastAction: text("action", {
      enum: [
        "RAISE",
        "AMEND",
        "REPLACED",
        "TRANSFERRED",
        "EXHAUSTION_UPDATED",
        "FULLY_EXHAUSTED",
        "FULLY_UN_EXHAUSTED",
        "STATUS_CHANGE",
        "TRANSFERRED",
        "TRANSPORT_DETAILS_UPDATED",
        "AUDIT_COMMENT",
        "CERTIFICATE_COLLECTED",
        "PRE_VALIDATION_CHECK",
        "RESPONSE_RECEIVED",
      ],
    }),
    //lastActionHistoryId: ulidId(),
    nzXmlURI: text("nzxml_uri"),
    snapshotURI: text("snapshot_uri"),

    // AUDIT FIELDS
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by", { length: email_length }), // ID for user
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by", { length: email_length }), // ID for user
    updatedByProfileName: varchar("updated_by_profile", { length: 20 }), // B2G Profile name used by user
    updatedBySystem: varchar("updated_by_system", { length: id_length }), // ID for user
    copiedFromId: varchar("copied_from_id", {
      length: id_length,
    }), // transferRequest.id
    attributes: jsonb("attributes"), // JSON object with additional configurable header level attributes
    // NON-SUBMISSION ITEMS
    submittedAt: timestamp("submitted_at", { withTimezone: true }), // Most recent submitted date
    submittedBy: varchar("submitted_by", { length: email_length }), // Most recent submitted by user
    approvedAt: timestamp("approved_at", { withTimezone: true }), // Most recent approved date
    approvedBy: varchar("approved_by", { length: email_length }), // Most recent approved by user
    auditedAt: timestamp("audited_at", { withTimezone: true }), // Most recent audited date
    auditedBy: varchar("audited_by", { length: email_length }), // Most recent audited by user

    stateMachineExecutionArn: text("state_machine_execution_arn"),
    taskToken: text("task_token"), // Use when we are in the wait for verification step
  },
  (table) => {
    return {
      requestor: index("core_transfer_requestor_idx").on(
        table.consignorId,
        table.consigneeId,
      ),
      productGroupId: index("idx_transfer_request_product_group_id").on(
        table.productGroupId,
      ),
    }
  },
)
const exportRequestIdRef = () => exportRequest.id
export const exportRequest = coreExportSchema.table(
  "export_request",
  {
    // IDENTIFIERS
    id: ulidId(),
    requestorReference: text("requestor_reference"),
    certificate_number: text("certificate_number").notNull().unique(), // e.g. "NZL2025/REGID/123"
    uniqueConsignmentReference: text("unique_consignment_reference"),
    productGroupId: varchar("product_item_group_id", {
      length: id_length,
    })
      .notNull()
      .references(() => coreProductGroup.id, { onDelete: "cascade" }),
    templateId: varchar("template_version_id", {
      length: id_length,
    })
      .notNull()
      .references(() => templateRevision.id, { onDelete: "cascade" }),
    templateCode: varchar("template_code", { length: 20 }), // From template
    templateVersion: integer("template_version"), // From template
    certificate_type: text("certificate_type", {
      // From template
      enum: [
        "FREE_SALE",
        "FREE_SALE_STATEMENT",
        "LIVE_ANIMALS",
        "ORGANICS",
        "EXPORT_ELIGIBILITY_STATEMENT",
        "SUPPLEMENTARY",
        "SANITARY",
        "HALAL",
        "CATCH",
        "PHYTOSANITARY",
        "REEXPORT_PHYTOSANITARY",
        "OECD_SEED_VARIETAL",
        "NONCONSIGNMENT",
        "PRODUCT_ESTABLISHMENT_REGISTRATION_DOCUMENT",
        "OTHER_DOCUMENT",
      ],
    }),
    version: integer("version"),
    status: text("status", {
      // This enum needs to match the Status enum in the graphql
      enum: [
        "DRAFT",
        "RAISED",
        "CANCELLED",
        "RESUBMIT", // Deliberately corrected from REQUESTED_TO_RESUBMIT in ec schema
        "AMENDED",
        "APPROVED",
        "REVOKED",
        // "VERIFIED", Moved to substatus
        // "PROVISIONALLY_APPROVED", Removed, only applicable to live animals
        "ACKNOWLEDGED",
        "ACCEPTED",
        "REQUEST_REPLACEMENT", // Deliberately corrected from REQUESTED_TO_REPLACE in ec schema
        "PENDING_REPLACEMENT",
        "REVOKED",
        "REJECTED",
        "PARTIAL",
        "DETAINED",
        "REPLACED",
        "REPLACEMENT_AUTHORISED",
      ],
    }).notNull(),
    substatus: text(
      "substatus" /*, {
      enum: [
        "PENDING_VERIFICATION",
        "PENDING_CERTIFICATION",
        "REQUESTED_TO_REVOKE",
      ],
    }*/,
    ),
    reasonForStatusChange: text("reason_for_state_change"), // TODO: Determine if status history goes in request history or separate request status table
    stateMachineExecutionArn: text("state_machine_execution_arn"),
    // Use when we are in the wait for verification step
    taskToken: text("task_token"),
    destination_country: jsonb("destination_country"),
    transit_countries: jsonb("transit_countries"), // array of country code
    commodity: text("commodity").notNull(),

    // PARTIES: Registrations
    consignorRegId: varchar("consignor_id", {
      length: id_length,
    }), // TODO - add FK to registrations table in party schema
    consignorId: jsonb("consignor_blob").notNull(),
    consigneeId: jsonb("consignee_blob"),
    thirdPartyAccess: jsonb("third_party_access"), // Array of registration ulids with read only access to record
    // PARTIES: Verifying organisations
    verifyingAgencyId: varchar("verifying_agency_id", {
      // Unique identifier for Halal Agency or Phyto IVA
      length: id_length,
    }),

    certifyingAgencyId: varchar("certifying_agency_id", {
      // Unique identifier for Halal Agency or Phyto IVA
      length: id_length,
    }),

    // PHYTO-SPECIFIC FIELDS
    externalConsignor: jsonb("external_consignor"), // JSONB field for external consignor details

    // PARTIES: Free text (CoreExternalParty)
    // JSON object with attributes id, name, address.lines, address.city, address.postCode, address.state, address.countryCode, representative
    importer: jsonb("importer"),
    customsTransitAgent: jsonb("customs_transit_agent"),
    destinationParty: jsonb("destination_party"),
    thirdPartyConsignor: jsonb("third_party_consignor"),
    reasonCategories: jsonb("reason_categories"), // Array of reasons selected by user
    overseasAuthorityInvolved: boolean("overseas_authority_involved"), // Nullable boolean flag for replacement

    // CERTIFICATE DELIVERY ATTRIBUTES
    urgency: jsonb("urgency"),
    signingOffice: jsonb("signing_office"),
    deliveryDetails: jsonb("delivery_details"),
    deliveryDispatchDetails: jsonb("delivery_dispatch_details"),
    approvedCertificateEmailAddresses: jsonb(
      "approved_certificate_email_addresses",
    ), // Array of email addresses

    // OTHER ATTRIBUTES
    transport: jsonb("transport"), // Contains CoreTransport GraphQL
    // Container goes on product group, as can be at consignment or product level
    officialStatement: text("official_statement"),
    unofficialInformation: text("unofficial_information"),
    additionalDeclaration: text("additional_declaration"),
    attributes: jsonb("attributes"), // JSON object with additional configurable header level attributes

    // AUDIT FIELDS
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by", { length: id_length }), // User ID
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by", { length: id_length }), // User ID
    updatedByProfileName: varchar("updated_by_profile", { length: 20 }), // B2G Profile Name used by user
    updatedBySystem: varchar("updated_by_system", { length: id_length }), // ID for user
    copiedFromId: varchar("copied_from_id", {
      length: id_length,
    }), // export_request.id

    officialPrinted: boolean("official_printed").default(false),
    /**
     * To enable history triggers
     */
    lastAction: text("action", {
      enum: [
        "RAISE",
        "AMEND",
        "REPLACED",
        "TRANSFERRED",
        "EXHAUSTION_UPDATED",
        "FULLY_EXHAUSTED",
        "FULLY_UN_EXHAUSTED",
        "STATUS_CHANGE",
        "SSNS_UPDATED",
        "CERTIFICATE_COLLECTED",
        "RESPONSE_RECEIVED",
      ],
    }),
    //lastActionHistoryId: ulidId(),
    nzXmlURI: text("nzxml_uri"),
    replacesId: varchar("replaces_id", {
      length: id_length,
    }).references(exportRequestIdRef, { onDelete: "set null" }), // For tracking replacement requests

    replacedById: varchar("replacedby_id", {
      length: id_length,
    }).references(exportRequestIdRef, { onDelete: "set null" }), // For tracking replacement requests

    snapshotURI: text("snapshot_uri"),

    // NON-SUBMISSION ITEMS
    submittedAt: timestamp("submitted_at", { withTimezone: true }), // Most recent submitted date
    submittedBy: varchar("submitted_by", { length: id_length }), // Most recent submitted by user ID
    approvedAt: timestamp("approved_at", { withTimezone: true }), // Most recent approved date
    approvedBy: varchar("approved_by", { length: id_length }), // Most recent approved by user ID
  },
  (table) => {
    return {
      requestor: index("core_export_requestor_idx").on(table.consignorId),
      productGroupId: index("idx_export_request_product_group_id").on(
        table.productGroupId,
      ),
    }
  },
)

export const exportDeclarationStatement = coreExportSchema.table(
  "declaration_statement",
  {
    id: ulidId(),
    exportRequestId: varchar("export_request_id", {
      length: id_length,
    })
      .notNull()
      .references(() => exportRequest.id, { onDelete: "cascade" }),

    selected: boolean("selected").notNull(),
    statementCode: integer("statement_code").notNull(),

    // AUDIT FIELDS
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => {
    return {
      requestStatement: index("core_declaration_statement_idx").on(
        table.exportRequestId,
        table.id,
      ),
    }
  },
)

export const exportDeclarationStatementInput = coreExportSchema.table(
  "declaration_statement_input",
  {
    id: ulidId(),
    declarationStatementId: varchar("declaration_statement_id", {
      length: id_length,
    })
      .notNull()
      .references(() => exportDeclarationStatement.id, { onDelete: "cascade" }),

    inputSequenceNumber: integer("sequence_number").notNull(),
    inputValue: varchar("input_value", { length: 256 }),

    // AUDIT FIELDS
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => {
    return {
      requestor: index("core_declaration_input_idx").on(
        table.declarationStatementId,
      ),
    }
  },
)

export const stationerySerialNumber = coreExportSchema.table(
  "stationery_serial_number",
  {
    id: ulidId(),
    exportRequestId: varchar("export_request_id", {
      length: id_length,
    })
      .notNull()
      .references(() => exportRequest.id, { onDelete: "cascade" }),
    sequenceNumber: integer("sequence_number").notNull(),
    stationerySerialNumber: text("stationery_serial_number").notNull(),
    active: boolean("active").notNull().default(true),
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by", { length: id_length }),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by", { length: id_length }),
  },
  (table) => {
    return {
      exportRequestIdx: index("stationery_serial_number_export_request_idx").on(
        table.exportRequestId,
      ),
      serialNumberIdx: index("stationery_serial_number_idx").on(
        table.stationerySerialNumber,
      ),
    }
  },
)

export const exportRequestOutput = coreExportSchema.table(
  "export_request_output",
  {
    id: ulidId(), // cro-<ulid> todo: match this for the history table
    exportRequestId: varchar("export_request_id", {
      length: id_length,
    })
      .notNull()
      .references(() => exportRequest.id, { onDelete: "cascade" }),
    fileType: text("file_type", { enum: ["XML", "PDF"] }),
    certificateType: text("certificate_type"),
    assuranceOutputType: text("assurance_output_type", {
      enum: ["DRAFT", "OFFICIAL", "COPY", "EXTRACT"],
    }),
    uri: text("uri"),
    certificateNumberSuffix: text("certificate_number_suffix"), // VI1/COO
    status: text("status", { enum: ["SUCCESS", "FAILED"] }),
    generatedAt: timestamp("generated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
)
